import { useEffect, useMemo, useState } from "react";
import { format, addDays } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { usePaymentCategories } from "@/hooks/payments/usePaymentCategories";
import { usePaymentMethods } from "@/hooks/payments/usePaymentMethods";
import { useCreateDebt } from "@/hooks/admin/useDebtMutations";

interface CreateDebtDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  userId: string;
  gymId: string;
  userName?: string;
}

export const CreateDebtDialog = ({
  open,
  onOpenChange,
  userId,
  gymId,
  userName,
}: CreateDebtDialogProps) => {
  const { categories, isLoading } = usePaymentCategories(gymId);
  const { methods } = usePaymentMethods(gymId);
  const createDebt = useCreateDebt();

  const [categoryId, setCategoryId] = useState<string>("");
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [paidAmount, setPaidAmount] = useState<number>(0);
  const [paymentMethod, setPaymentMethod] = useState<string>("");
  const [graceDays, setGraceDays] = useState<number>(7);
  const [dueDate, setDueDate] = useState<string>(
    format(addDays(new Date(), 7), "yyyy-MM-dd")
  );
  const [dueTouched, setDueTouched] = useState(false);

  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === categoryId) || null,
    [categories, categoryId]
  );

  // Auto-fill total when category changes
  useEffect(() => {
    if (selectedCategory) {
      setTotalAmount(selectedCategory.amount);
    }
  }, [selectedCategory]);

  // Auto-update due date when grace days change unless manager edited it
  useEffect(() => {
    if (!dueTouched) {
      setDueDate(format(addDays(new Date(), graceDays), "yyyy-MM-dd"));
    }
  }, [graceDays, dueTouched]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setCategoryId("");
      setTotalAmount(0);
      setPaidAmount(0);
      setPaymentMethod("");
      setGraceDays(7);
      setDueDate(format(addDays(new Date(), 7), "yyyy-MM-dd"));
      setDueTouched(false);
    }
  }, [open]);

  const owed = Math.max(0, (totalAmount || 0) - (paidAmount || 0));
  const needsPaymentMethod = paidAmount > 0;
  const canSubmit =
    !!selectedCategory &&
    totalAmount > 0 &&
    paidAmount >= 0 &&
    paidAmount <= totalAmount &&
    owed > 0 &&
    !!dueDate &&
    (!needsPaymentMethod || !!paymentMethod);

  const handleSubmit = async () => {
    if (!selectedCategory) return;
    await createDebt.mutateAsync({
      user_id: userId,
      gym_id: gymId,
      category_id: selectedCategory.id,
      category_name: selectedCategory.name,
      category_validity_days: selectedCategory.validity_days,
      total_amount: totalAmount,
      paid_amount: paidAmount,
      grace_days: graceDays,
      due_date: dueDate,
      payment_method: needsPaymentMethod ? paymentMethod : undefined,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton className="bg-background">
        <DialogHeader>
          <DialogTitle>Crear deuda</DialogTitle>
          <DialogDescription>
            {userName ? `Registrar deuda para ${userName}.` : "Registrar deuda del socio."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Categoría</Label>
            <Select value={categoryId} onValueChange={setCategoryId} disabled={isLoading}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar categoría" />
              </SelectTrigger>
              <SelectContent className="bg-popover">
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name} — ${c.amount.toLocaleString()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Monto total</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={totalAmount === 0 ? "" : totalAmount}
                onChange={(e) => setTotalAmount(Number(e.target.value) || 0)}
              />
            </div>
            <div className="space-y-2">
              <Label>Monto pagado</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={paidAmount === 0 ? "" : paidAmount}
                onChange={(e) => setPaidAmount(Number(e.target.value) || 0)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Monto adeudado</Label>
            <Input value={`$${owed.toLocaleString()}`} readOnly className="bg-muted" />
          </div>

          {needsPaymentMethod && (
            <div className="space-y-2">
              <Label>Método de pago (para el monto pagado)</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar método de pago" />
                </SelectTrigger>
                <SelectContent className="bg-popover">
                  {(methods || []).map((m) => (
                    <SelectItem key={m.id} value={m.name}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Se registrará un pago por ${paidAmount.toLocaleString()} en el historial y arqueo.
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Plazo de gracia (días)</Label>
              <Input
                type="number"
                min="0"
                value={graceDays}
                onChange={(e) => setGraceDays(Number(e.target.value) || 0)}
              />
            </div>
            <div className="space-y-2">
              <Label>Fecha de vencimiento</Label>
              <DatePicker
                value={dueDate}
                onChange={(v) => {
                  setDueDate(v);
                  setDueTouched(true);
                }}
                placeholder="Seleccionar fecha"
              />
            </div>
          </div>

          {paidAmount > totalAmount && (
            <p className="text-xs text-destructive">
              El monto pagado no puede ser mayor al total.
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit || createDebt.isPending}>
              {createDebt.isPending ? "Guardando..." : "Crear deuda"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
